<script>
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import { mapGetters } from "vuex";
import { capitalize } from "@/services/helper";
import {
  landingUrl,
  isMonitoringEnabled,
  isProtocolsEnabled,
  isProgramsEnabled,
  arePrescriptionsForDoctorOnly,
  isPrescriptionsHiddenForDoctor,
  areFollowupsRestrictedForBackoffice,
  areProtocolsRestrictedForBackoffice,
  areValidationsRestrictedForBackoffice,
  isOrdersCrmEnabled,
} from "@/services/config";

export default {
  name: "Home",
  data() {
    return {
      isProgramsEnabled,
      isOrdersCrmEnabled,
    };
  },
  components: {
    BaseSubNav,
  },
  computed: {
    ...mapGetters([
      "isDoctor",
      "isPatient",
      "isAdmin",
      "isAssistant",
      "isManager",
      "user",
      "isAdmissionist",
    ]),
    isPrescriptionsVisible() {
      if (isPrescriptionsHiddenForDoctor && this.isDoctor) return false;
      if (arePrescriptionsForDoctorOnly) {
        return this.isDoctor;
      }
      return !this.isDoctor;
    },
    isFollowupsVisible() {
      if (this.isPatient || this.isAdmissionist || this.isDoctor) return false;
      if (!isMonitoringEnabled) return false;
      if (areFollowupsRestrictedForBackoffice) {
        return !this.isAdmin && !this.isAssistant && !this.isManager;
      }
      return true;
    },
    isProtocolsVisible() {
      if (!isProtocolsEnabled) return false;
      if (this.isPatient || this.isAdmissionist || this.isDoctor) return false;
      if (areProtocolsRestrictedForBackoffice) {
        return !this.isAdmin && !this.isAssistant && !this.isManager;
      }
      return true;
    },
    isValidationsVisible() {
      if (this.isPatient || this.isAdmissionist || this.isDoctor) return false;
      if (areValidationsRestrictedForBackoffice) {
        return !this.isAdmin && !this.isAssistant && !this.isManager;
      }
      return true;
    },
    // El admin siempre ve Estadísticas; el asistente, solo si tiene el permiso
    // habilitado (statistics.view, análogo al CRM de órdenes).
    isStatisticsVisible() {
      return (
        this.isAdmin ||
        (this.user?.permissions || []).includes("statistics.view")
      );
    },
    userName() {
      if (!this.user || !this.user.firstName) return "";
      return `Hola, ${capitalize(this.user.firstName)}`;
    },
  },
  methods: {
    handleAgendaClick() {
      window.open(landingUrl, "_blank");
    },
  },
};
</script>

<template lang="pug">
  div
    BaseSubNav
      template(#title)
        | {{ userName }}
    .home-buttons
      router-link.home-btn(:to="{ name: 'dashboard-alerts' }" v-if="isFollowupsVisible")
        micon(name="pending_actions" width="52" height="52")
        span Seguimientos
      router-link.home-btn(:to="{ name: 'patient-me' }" v-if="isPatient")
        micon(name="pending_actions" width="52" height="52")
        span Órdenes médicas
      router-link.home-btn(v-if="!isPatient" :to="{ name: 'patient' }")
        micon(name="people" width="52" height="52")
        span Pacientes
      router-link.home-btn(v-if="isProtocolsVisible" :to="{ name: 'treatment' }")
        micon(name="assignment" width="52" height="52")
        span Protocolos
      router-link.home-btn(v-if="!isPatient" :to="{ name: 'appointment-calendar' }")
        micon(name="calendar" width="52" height="52")
        span Agenda
      // Acá se agrega un botón porque no se puede hacer un condicional con router-link para pacientes
      button.home-btn(v-else @click="handleAgendaClick")
        micon(name="calendar" width="52" height="52")
        span Agenda
      router-link.home-btn(v-if="isDoctor" :to="{ name: 'appointment-schedule' }")
        micon(name="list" width="52" height="52")
        span Atenciones
      router-link.home-btn(v-if="isAdmin && isProgramsEnabled" :to="{ name: 'administration' }")
        micon(name="admin_panel_settings" width="52" height="52")
        span Programas
      router-link.home-btn(v-if="isStatisticsVisible" :to="{ name: 'stats' }")
        micon(name="insert_chart_outlined" width="52" height="52")
        span Estadísticas
      router-link.home-btn(v-if="isAdmin" :to="{ name: 'users-list' }")
        micon(name="admin_panel_settings" width="52" height="52")
        span Administración de usuarios
      router-link.home-btn(v-if="isAdmin" :to="{ name: 'settings' }")
        micon(name="settings" width="52" height="52")
        span Configuración
      router-link.home-btn(v-if="isValidationsVisible" :to="{ name: 'validation' }")
        micon(name="check" width="52" height="52")
        span Validaciones
      router-link.home-btn(v-if="isPrescriptionsVisible" :to="{ name: 'prescription' }")
        micon(name="medication" width="52" height="52")
        span Prescripciones
      router-link.home-btn(v-if="isAdmin" :to="{ name: 'contracts' }")
        micon(name="description" width="52" height="52")
        span Contratos
      router-link.home-btn(v-if="isAdmin" :to="{ name: 'prescription-queue' }")
        micon(name="medication" width="52" height="52")
        span Bandeja de recetas
      router-link.home-btn(v-if="isOrdersCrmEnabled" v-auth="'orders-crm.view'" :to="{ name: 'orders-crm' }")
        micon(name="assignment_turned_in" width="52" height="52")
        span CRM de órdenes
      router-link.home-btn(v-if="isAdmin || isAssistant" :to="{ name: 'doctor-location' }")
        micon(name="location_on" width="52" height="52")
        span Ubicación médicos
      // Acá se agrega un botón en lugar de router-link porque por el momento no va a tener funcionamiento y el router-link si o si debe tener un :to
      button.home-btn(v-if="isPatient")
        micon(name="calendar" width="52" height="52")
        span Mis citas
      router-link.home-btn(v-if="isPatient" :to="{ name: 'patient-prescription-request' }")
        micon(name="medication" width="52" height="52")
        span Pedir receta
</template>

<style lang="scss" scoped>
.home-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  width: 100%;
}

.home-btn {
  flex: 0 0 calc(25% - 1.5rem);
  aspect-ratio: 1 / 0.6;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 10px;
  padding: 2rem;
  font-size: 1.1rem;
  color: #040055;
  text-decoration: none;
  box-shadow: 0 1px 4px #0001;
  transition: all 0.2s ease;
  font-family: "Roboto", Helvetica, Arial, sans-serif;
  font-weight: bold;
  border: 0px;
}

.home-btn:hover {
  background: #e3e8f0;
  box-shadow: 0 4px 12px #0002;
}

.home-btn span {
  margin-top: 0.5rem;
}
</style>
